import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LangProvider } from "./contexts/LangContext";
import { CartProvider } from "./contexts/CartContext";
import { PublicLayout, B2BLayout, AdminLayout } from "./layouts/Layouts";
import { Toaster } from "./components/ui/sonner";

import Home from "./pages/Home";
import AIRicette from "./pages/AIRicette";
import BoxShop from "./pages/BoxShop";
import BoxDetail from "./pages/BoxDetail";
import Vini from "./pages/Vini";
import Scopri from "./pages/Scopri";
import Regione from "./pages/Regione";
import Abbonamenti from "./pages/Abbonamenti";
import Esperienze from "./pages/Esperienze";
import EsperienzaDetail from "./pages/EsperienzaDetail";
import TV from "./pages/TV";
import TVEpisode from "./pages/TVEpisode";
import Privacy from "./pages/Privacy";
import Cookie from "./pages/Cookie";
import AdminNewsletter from "./pages/admin/AdminNewsletter";
import CookieBanner from "./components/CookieBanner";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Club from "./pages/Club";
import ClubSuccess from "./pages/ClubSuccess";
import ClubAuctionDetail from "./pages/ClubAuctionDetail";
import AreaUtente from "./pages/AreaUtente";
import Supporto from "./pages/Supporto";
import MediaKit from "./pages/MediaKit";
import Franchising from "./pages/Franchising";
import Rewards from "./pages/Rewards";
import Magazine from "./pages/Magazine";
import Pitch from "./pages/Pitch";
import Login from "./pages/Login";
import Register from "./pages/Register";

import B2BHome from "./pages/b2b/B2BHome";
import B2BDashboard from "./pages/b2b/B2BDashboard";
import B2BWines from "./pages/b2b/B2BWines";
import B2BWineDetail from "./pages/b2b/B2BWineDetail";
import B2BSommelier from "./pages/b2b/B2BSommelier";
import B2BMarketing from "./pages/b2b/B2BMarketing";
import B2BProposeAuction from "./pages/b2b/B2BProposeAuction";
import B2BQuotes from "./pages/b2b/B2BQuotes";
import B2BAnagrafica from "./pages/b2b/B2BAnagrafica";
import B2BSellLot from "./pages/b2b/B2BSellLot";
import B2BLotsMarketplace from "./pages/b2b/B2BLotsMarketplace";
import B2BMyLots from "./pages/b2b/B2BMyLots";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminCatalog from "./pages/admin/AdminCatalog";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminBrokers from "./pages/admin/AdminBrokers";
import AdminCRM from "./pages/admin/AdminCRM";
import AdminAlerts from "./pages/admin/AdminAlerts";
import AdminAnalyst from "./pages/admin/AdminAnalyst";
import AdminClub from "./pages/admin/AdminClub";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminMediaRequests from "./pages/admin/AdminMediaRequests";
import AdminMarketing from "./pages/admin/AdminMarketing";
import AdminAuctions from "./pages/admin/AdminAuctions";
import AdminFranchising from "./pages/admin/AdminFranchising";
import AdminB2BLots from "./pages/admin/AdminB2BLots";
import AdminImports from "./pages/admin/AdminImports";
import GestionaleLogin from "./pages/GestionaleLogin";
import GestionalePitch from "./pages/GestionalePitch";
import GestionaleDashboard from "./pages/GestionaleDashboard";
import Splash from "./pages/Splash";
import B2CRedirect from "./pages/B2CRedirect";
import { IS_MZN300 } from "./config/brand";
import GestionaleModule from "./pages/GestionaleModule";
import SuppliersPage from "./pages/gestionale/SuppliersPage";
import QuotationsPage from "./pages/gestionale/QuotationsPage";
import ContainersPage from "./pages/gestionale/ContainersPage";
import MapaCasesPage from "./pages/gestionale/MapaCasesPage";
import CommissionsPage from "./pages/gestionale/CommissionsPage";
import CrmLeadsPage from "./pages/gestionale/CrmLeadsPage";

function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              {/* Root — depends on brand:
                  - amazonico3000.com → Splash gate (B2C link is external to mzn300.com)
                  - mzn300.com        → Home B2C directly (skips splash)
              */}
              <Route path="/" element={IS_MZN300 ? <PublicLayout /> : <Splash />}>
                {IS_MZN300 && <Route index element={<Home />} />}
              </Route>
              {/* B2B marketing homepage (standalone, uses MainNav) */}
              <Route path="/business" element={<B2BHome />} />
              {/* Legacy B2C routes: on amazonico3000.com now redirect to mzn300.com */}
              {!IS_MZN300 && (
                <>
                  <Route path="/b2c" element={<B2CRedirect />} />
                  <Route path="/b2c-home" element={<B2CRedirect />} />
                </>
              )}
              <Route element={<PublicLayout />}>
                {IS_MZN300 && (
                  <>
                    <Route path="/b2c" element={<Home />} />
                    <Route path="/b2c-home" element={<Home />} />
                  </>
                )}
                <Route path="/ai-ricette" element={<AIRicette />} />
                <Route path="/box" element={<BoxShop />} />
                <Route path="/box/:id" element={<BoxDetail />} />
                <Route path="/vini" element={<Vini />} />
                <Route path="/scopri" element={<Scopri />} />
                <Route path="/scopri/regione/:slug" element={<Regione />} />
                <Route path="/abbonamenti" element={<Abbonamenti />} />
                <Route path="/esperienze" element={<Esperienze />} />
                <Route path="/esperienze/:id" element={<EsperienzaDetail />} />
                <Route path="/tv" element={<TV />} />
                <Route path="/tv/:id" element={<TVEpisode />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookie" element={<Cookie />} />
                <Route path="/carrello" element={<Checkout />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/club" element={<Club />} />
                <Route path="/club/auctions/:id" element={<ClubAuctionDetail />} />
                <Route path="/club/success" element={<ClubSuccess />} />
                <Route path="/area-utente" element={<AreaUtente />} />
                <Route path="/supporto" element={<Supporto />} />
                <Route path="/media-kit" element={<MediaKit />} />
                <Route path="/franchising" element={<Franchising />} />
                <Route path="/punti" element={<Rewards />} />
                <Route path="/rivista" element={<Magazine />} />
                <Route path="/pitch" element={<Pitch />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
              </Route>

              <Route element={<B2BLayout />}>
                <Route path="/b2b" element={<B2BHome hideNav />} />
                <Route path="/b2b/dashboard" element={<B2BDashboard />} />
                <Route path="/b2b/wines" element={<B2BWines />} />
                <Route path="/b2b/wines/:id" element={<B2BWineDetail />} />
                <Route path="/b2b/sommelier" element={<B2BSommelier />} />
                <Route path="/b2b/marketing" element={<B2BMarketing />} />
                <Route path="/b2b/auctions/propose" element={<B2BProposeAuction />} />
                <Route path="/b2b/quotes" element={<B2BQuotes />} />
                <Route path="/b2b/anagrafica" element={<B2BAnagrafica />} />
                <Route path="/b2b/lots/sell" element={<B2BSellLot />} />
                <Route path="/b2b/lots/marketplace" element={<B2BLotsMarketplace />} />
                <Route path="/b2b/lots/mine" element={<B2BMyLots />} />
              </Route>

              {/* Standalone gestionale login (no Public/Admin layout — full-screen branded) */}
              <Route path="/gestionale" element={<GestionaleLogin />} />
              <Route path="/gestionale/pitch" element={<GestionalePitch />} />
              <Route path="/gestionale/dashboard" element={<GestionaleDashboard />} />
              <Route path="/gestionale/suppliers" element={<SuppliersPage />} />
              <Route path="/gestionale/quotations" element={<QuotationsPage />} />
              <Route path="/gestionale/containers" element={<ContainersPage />} />
              <Route path="/gestionale/mapa-cases" element={<MapaCasesPage />} />
              <Route path="/gestionale/commissions" element={<CommissionsPage />} />
              <Route path="/gestionale/crm-leads" element={<CrmLeadsPage />} />
              <Route path="/gestionale/modulo/:slug" element={<GestionaleModule />} />

              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/approvals" element={<AdminApprovals />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/customers" element={<AdminCustomers />} />
                <Route path="/admin/catalog" element={<AdminCatalog />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/club" element={<AdminClub />} />
                <Route path="/admin/media" element={<AdminMediaRequests />} />
                <Route path="/admin/marketing" element={<AdminMarketing />} />
                <Route path="/admin/auctions" element={<AdminAuctions />} />
                <Route path="/admin/franchising" element={<AdminFranchising />} />
                <Route path="/admin/b2b-lots" element={<AdminB2BLots />} />
                <Route path="/admin/imports" element={<AdminImports />} />
                <Route path="/admin/newsletter" element={<AdminNewsletter />} />
                <Route path="/admin/brokers" element={<AdminBrokers />} />
                <Route path="/admin/crm" element={<AdminCRM />} />
                <Route path="/admin/alerts" element={<AdminAlerts />} />
                <Route path="/admin/analyst" element={<AdminAnalyst />} />
              </Route>
            </Routes>
            <Toaster position="top-right" richColors />
            <CookieBanner />
          </BrowserRouter>
        </CartProvider>
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
